
import store from "../../store/store";
import { gameActions } from "../Game/gameSlice";
import "./Lobby.scss";
import { GameSettings } from "../../models/Game";
import Button from "../common/Button";
import Card from "../Card/Card";
import classNames from "classnames";
import { GameStatus } from "../../models/GameStatus";

export default function LobbySettings() {

  const gameState = store.getState().game;
  const gameListState = store.getState().gameList;
  const cardSetState = store.getState().cardSet;

  const gameAdmins: string[] = [];

  Object.values(gameListState.gameList).forEach(it => {
    if (it.gameId && it.gameId === gameState.gameId) {
      gameAdmins.push(...it.gameAdmins);
    }
  });

  const isHost = Boolean(gameState.hostId === gameState.player?.data.playerId
    || (gameAdmins && gameState.player?.data.playerId && gameAdmins.includes(gameState.player?.data.playerId)));

  // true gdainti/test admin only, not just this game's host - gates settings players must not see at all
  const isAdmin = Boolean(gameState.player?.data.playerId && gameListState.admins.includes(gameState.player.data.playerId));

  function start(e: any) {
    if (gameState.player) {
      store.dispatch(gameActions.sendStart({
        gameId: gameState.gameId,
        playerId: gameState.player?.data.playerId,
      }));
    }
  }

  function updateSettings() {
    onSettingChange(gameState.settings, true);
    cancelLobbySettings();
  }

  function endSession(e: any) {
    if (gameState.player) {
      store.dispatch(gameActions.sendEndSession({
        gameId: gameState.gameId,
        adminId: gameState.player?.data.playerId
      }));
    }
  }

  function cancelLobbySettings() {
    window.location.reload();
  }

  function notify(e: any) {
    if (gameState.player) {
      store.dispatch(gameActions.sendNotifyStart({
        gameId: gameState.gameId,
        playerId: gameState.player?.data.playerId,
      }));
    }
  }

  function handleSubmit(e: any) {
    e.preventDefault();
  }

  function onSettingChange(settings: Partial<GameSettings>, forceSave: boolean = false) {

    const updatedSettings = {
      gameId: gameState.gameId,
      playerId: gameState.player?.data.playerId,
      setting: { ...settings, /*  ...maxTurnsSetting */ }
    };

    store.dispatch(
      gameState.status === GameStatus.LOBBY_SETTINGS && !forceSave
        ? gameActions.updateSettings(updatedSettings)
        : gameActions.sendSettings(updatedSettings)
    );
  }

  function onCheckboxChange(e: any, targetCardSet: string) {
    const target = e.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const setIndex = gameState.settings.cardSet.indexOf(targetCardSet);
    const cardSet = [...gameState.settings.cardSet];
    if (setIndex !== -1 && !value) {
      cardSet.splice(setIndex, 1);
    }
    if (setIndex === -1 && value) {
      cardSet.push(targetCardSet);
    }
    store.dispatch(gameActions.sendSettings({ gameId: gameState.gameId, setting: { cardSet: cardSet } }));
  }

  const isSettingsDisabled = !isHost;
  const isCardSetsDisabled = !!gameState.settings.useExistingDeck && gameState.settings.deckSize > 0;

  const MIN_PLAYER_LENGTH = process.env.NODE_ENV === "production"
    ? 3
    : 2;

  // hostOnly: warnings only the host can act on - players see the rest
  const startWarnings: { msg: string, hostOnly: boolean }[] = [];

  if (!gameState.settings.handSize || gameState.settings.handSize < 2 || gameState.settings.handSize > 10) {
    startWarnings.push({ msg: "Hand size must be between 2 and 10", hostOnly: true });
  }
  if (gameState.players.length < MIN_PLAYER_LENGTH) {
    startWarnings.push({ msg: `Not enough players (min ${MIN_PLAYER_LENGTH}), invite your friends!`, hostOnly: false });
  }
  if (gameState.settings.cardSet.length === 0) {
    startWarnings.push({ msg: "At least one cardSet set must be selected", hostOnly: true });
  }

  const isStartEnabled = startWarnings.length === 0;
  const visibleWarnings = startWarnings.filter(it => isHost || !it.hostOnly);

  return (
    <div className="lobby_settings">
      <form className="lobby_settings_form" onSubmit={handleSubmit}>
        <fieldset disabled={isSettingsDisabled} className="win-conditions">
          <h3>win Conditions</h3>
          <label>
            <div>hand size:</div>
            <input
              onChange={e => onSettingChange({ handSize: e.target.valueAsNumber })}
              value={gameState.settings.handSize || ''}
              type="number"
              max="10"
              min="2"
              step="1"
              name="hand size" />
          </label>
          <label>
            <div>max rounds:</div>
            <input
              onChange={e => onSettingChange({ maxRounds: e.target.valueAsNumber })}
              value={gameState.settings.maxRounds || ''}
              min="0"
              max="100"
              step="1"
              type="number" name="max rounds" />
          </label>
          <label>
            <div>max score:</div>
            <input
              onChange={e => onSettingChange({ maxScore: e.target.valueAsNumber })}
              value={gameState.settings.maxScore || ''}
              min="0"
              max="100"
              step="1"
              type="number" name="max score" />
          </label>
          <label>
            <div>max cards:</div>
            <input
              onChange={e => onSettingChange({ maxCards: e.target.valueAsNumber })}
              value={gameState.settings.maxCards || ''}
              min="0"
              max="100"
              step="1"
              type="number" name="max cards" />
          </label>
          <label>
            <div>max turns:</div>
            <input
              onChange={e => onSettingChange({ maxTurns: e.target.valueAsNumber })}
              value={gameState.settings.maxTurns || ''}
              min="0"
              max="100"
              step="1"
              type="number" name="max turns" />
          </label>
        </fieldset>

        <fieldset className="card-sets" disabled={isSettingsDisabled}>
          <h3>card sets</h3>
          {cardSetState.sets && cardSetState.sets.map((cardSet, i) => {
            return (
              <label key={i}>
                <input type="checkbox"
                  disabled={isCardSetsDisabled}
                  checked={gameState.settings.cardSet.includes(cardSet.name) || false}
                  onChange={(e) => onCheckboxChange(e, cardSet.name)}
                />
                <div>{cardSet.name} [{cardSet.cardsAmount}]</div>
              </label>
            )
          })}

        </fieldset>

        <fieldset className="card-sets-existing" disabled={isSettingsDisabled}>
          <h3>options</h3>
          <label>
            <input type="checkbox"
              disabled={!gameState.settings.deckSize}
              checked={gameState.settings.useExistingDeck || false}
              onChange={(e) => onSettingChange({ useExistingDeck: e.target.checked })}
            />
            <div>existing deck [{gameState.settings.deckSize}]</div>
          </label>
          <label>
            <input type="checkbox"
              checked={gameState.settings.asyncPlay || false}
              onChange={(e) => onSettingChange({ asyncPlay: e.target.checked })}
            />
            <div>async play (notify stages, random first storyteller)</div>
          </label>

          {isAdmin && <>
            <label>
              <input type="checkbox"
                checked={gameState.settings.notifyDiscord || false}
                onChange={(e) => onSettingChange({ notifyDiscord: e.target.checked })}
              />
              <div>notify discord</div>
            </label>

            <label>
              <input type="checkbox"
                checked={gameState.settings.notifyTelegram || false}
                onChange={(e) => onSettingChange({ notifyTelegram: e.target.checked })}
              />
              <div>notify telegram</div>
            </label>
          </>}

        </fieldset>


        <fieldset className="custom" disabled={isSettingsDisabled}>

          <h3>custom Rules</h3>
          <label>
            <textarea
              onChange={e => onSettingChange({ customRules: e.target.value })}
              value={gameState.settings.customRules || ''}
              name="max rounds" />
          </label>

        </fieldset>

        <fieldset disabled={isSettingsDisabled} className="card-backs">

          <h3>card Backs</h3>
          <div className="card-backs_list">
            {gameState.cardBacks.map((it, i) => {

              const cardClass = classNames({
                'game-card_selected': it === gameState.settings.cardBack,
              });

              return (
                <Card
                  classes={cardClass}
                  key={i}
                  card={{ cardId: `${i}`, src: it }}
                  cards={gameState.cardBacks.map((it, cardId) => {
                    return { cardId: `${cardId}`, src: it }
                  })}
                  isLongPressEnabled={true}
                  isSelectable={true}
                  onSelect={() => onSettingChange({ cardBack: it })}
                />
              )
            })}
          </div>
        </fieldset>

      </form>

      <div className="lobby_settings_errors">
        {visibleWarnings.length > 0 && (
          <div className="lobby_settings_warnings" style={{ marginBottom: '10px', color: '#ff4d4f' }}>
            {visibleWarnings.map((it, i) => <div key={i}>{it.msg}</div>)}
          </div>
        )}
      </div>

      <div className="lobby_settings_buttons">

        {gameState.status !== GameStatus.LOBBY_SETTINGS && <>
          {isHost &&
            <Button
              text="Start Game"
              disabled={!isStartEnabled || isSettingsDisabled}
              onClick={start}
              classes="lobby_start"
              confirmPrompt={`Are you sure you want to start a new game?`}
            />
          }
          {isHost &&
            <Button
              text="Notify Start"
              disabled={isSettingsDisabled || gameState.hasNotifiedStart}
              onClick={notify} classes="green" />
          }
          {isHost &&
            <Button
              text="End Session"
              disabled={isSettingsDisabled}
              onClick={endSession}
              classes="danger"
              confirmPrompt={'Are you sure you want to end this session?'}
            />
          }
        </>}
        {gameState.status === GameStatus.LOBBY_SETTINGS && <>
          {isHost &&
            <Button text="Update settings" disabled={!isStartEnabled || isSettingsDisabled} onClick={updateSettings} classes="lobby_start" />
          }
          <Button text="Cancel" disabled={false} onClick={cancelLobbySettings} classes="danger" />
        </>}
      </div>

    </div>
  )
}
